/* eslint-disable no-console */
/* eslint-disable eqeqeq */
/* eslint-disable no-param-reassign */
/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
/* eslint-disable react/prop-types */
import classNames from 'classnames';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import CameraImg from 'Assets/images/pages/web-chat/cam.svg';
import HeadPhoneImg from 'Assets/images/pages/web-chat/head-phone.svg';
import MinimizeCancelImg from 'Assets/images/pages/web-chat/fullscreen.svg';
import ImgAvatarDefault from 'Assets/images/pages/web-chat/avatar-default.png';
import ImgSound from 'Assets/images/pages/web-chat/sound-wave.svg';
import moment from 'moment';
import { Image } from 'semantic-ui-react';

import VirtualBackgroundModal from './VirtualBackgroundModal/VirtualBackgroundModal';
import css from './ZoomView.less';
import VideoHeader from './VideoControls/VideoHeader/VideoHeader';
import Waiting from './VideoControls/Waiting/Waiting';
import Counter from './VideoControls/Counter/Counter';
import CameraOff from './VideoControls/CameraOff/CameraOff';
import useStartZoomSdk from './hooks/useStartZoomSdk';
import useParticipantChanged from './hooks/useParticipantChanged';
import useNetworkQuality from './hooks/useNetworkQuality';
import NetworkQualityWarning from './VideoControls/NetworkQualityWarning/NetworkQualityWarning';
import useDeviceChanged from './hooks/useDeviceChanged';
import MemberNameMinimized from './VideoControls/MemberNameMinimized/MemberNameMinimized';
import { EndReason } from './constants';
import useVisualBackground from './hooks/useVisualBackground';
import useConnectionChange from './hooks/useConnectionChange';
import useZoomVideoSdk from './hooks/useZoomVideoSdk';
import useMic from './hooks/useMic';
import useDragAndDrop from './hooks/useDragAndDrop';
import StatusJoinCall from './StatusJoinCall/StatusJoinCall';
import useActiveSpeaker from './hooks/useActiveSpeaker';
import VideoControls from './VideoControls/VideoControls';
import useShowToast from './hooks/useShowToast';
import Recording from './VideoControls/Recording/Recording';

const INTERVAL = 500;

function ZoomView({
  currentUserName,
  roomInfo,
  onCloseVideo,
  handleEndCall,
  displayName,
  avatar,
  zoomClient,
  showError,
  isAttentionAudio,
  setIsAttentionAudio,
  audioRef,
  logEvents,
  recording,
}) {
  const backgroundLocalstorage = localStorage.getItem('video-call-background');
  const [participantName] = useState(displayName);
  const [patientAlreadyJoined, setPatientAlreadyJoined] = useState(false);
  const [openVirtualBackground, setOpenVirtualBackground] = useState(false);
  const [selectedBg, setSelectedBg] = useState(backgroundLocalstorage ?? 'none');
  const [participantInit, setParticipantInit] = useState(false);
  const [flip, setFlip] = useState(false);
  const [startTimeExpireCall, setStartTimeExpireCall] = useState();
  const mediaStream = useMemo(() => zoomClient.getMediaStream(), [zoomClient]);

  const {
    isMinimized,
    onMouseDown,
    onMouseLeave,
    onMouseMove,
    onMouseUp,
    refContainer,
    resetPositionMinimized,
    setIsMinimized,
  } = useDragAndDrop();

  const { showToast } = useShowToast();

  const { toggleMic } = useMic({ mediaStream });
  const { speakingUsers } = useActiveSpeaker({ zoomClient });

  const { status } = useStartZoomSdk({
    zoomClient,
    zoomInfo: roomInfo,
    selfVideoContainerId: 'video-call-self',
    userName: currentUserName,
    showError,
    logEvents,
  });

  const { isCameraOff, startVideo, stopVideo } = useZoomVideoSdk({
    zoomClient,
    mediaStream,
    selfVideoContainerId: 'video-call-self',
    participantVideoContainerId: 'video-call-participant',
  });

  const leaveRoom = useCallback(
    async callback => {
      try {
        await Promise.allSettled([mediaStream.stopVideo(), mediaStream.stopAudio()]);
        await zoomClient.leave(zoomClient.isHost());
        callback();
      } catch (error) {
        await zoomClient.leave(zoomClient.isHost());
        callback();
      }
    },
    [mediaStream, zoomClient],
  );

  const { getCurrentUserParticipant, getOtherUserParticipant, participants } =
    useParticipantChanged({ zoomClient });

  const endCallWithoutApi = () => {
    leaveRoom(onCloseVideo);
  };

  useConnectionChange({
    zoomClient,
    endCall: endCallWithoutApi,
  });

  const patientJoined = participants?.length > 1;
  const currentUserParticipant = getCurrentUserParticipant();
  const otherUserParticipant = getOtherUserParticipant();
  const participantCameraOn = otherUserParticipant?.bVideoOn;

  const { isBlur, toggleBlur } = useVisualBackground({
    zoomClient,
    mediaStream,
    userId: currentUserParticipant?.userId,
    selfVideoContainerId: 'video-call-self',
  });

  const { networkQuality: yourNetworkQuality } = useNetworkQuality({
    zoomClient,
    userId: currentUserParticipant?.userId,
  });

  const supportVirtualBackground = useMemo(
    () => mediaStream.isSupportVirtualBackground(),
    [mediaStream],
  );

  const deviceAdded = useCallback(
    (type, previousDevices, currentDevices, addedDevices) => {
      const activeCam = mediaStream?.getActiveCamera();
      if (type === 'cam' && addedDevices?.map(e => e.deviceId).includes(activeCam) && isCameraOff) {
        startVideo();
      }
    },
    [startVideo, mediaStream, isCameraOff],
  );

  const deviceRemoved = useCallback(
    async (type, previousDevices, currentDevices, removedDevices) => {
      const activeCamera = mediaStream.getActiveCamera();
      const icon = type === 'cam' ? CameraImg : HeadPhoneImg;
      const deviceName = type === 'cam' ? 'camera' : 'microphone';
      const message = `No ${deviceName} detected. Please connect one and then restart your browser if necessary.`;
      if (type === 'cam') {
        if (removedDevices.find(e => e.deviceId === activeCamera) && !isCameraOff) {
          await stopVideo();
          if (currentDevices.length > 0) {
            startVideo(currentDevices?.[0].deviceId);
          }
        }
        if (currentDevices.length === 0) {
          showToast(icon, message, true);
        }
      }
    },
    [showToast, stopVideo, startVideo, mediaStream, isCameraOff],
  );

  const userDevices = useDeviceChanged({
    zoomClient,
    mediaStream,
    deviceAdded,
    deviceRemoved,
  });

  const handleCloseVideo = useCallback(
    reason => {
      leaveRoom(onCloseVideo);
      handleEndCall(reason);
    },
    [handleEndCall, onCloseVideo, leaveRoom],
  );

  useEffect(() => {
    if (patientJoined) {
      setPatientAlreadyJoined(true);
    }
  }, [patientJoined]);

  useEffect(() => {
    if (participantCameraOn) setParticipantInit(true);
  }, [participantCameraOn]);

  useEffect(() => {
    setStartTimeExpireCall(Date.now());
    return () => {
      leaveRoom(onCloseVideo);
    };
  }, [leaveRoom, onCloseVideo]);

  useEffect(() => {
    if (patientAlreadyJoined) audioRef.current?.pause();
    else audioRef.current?.play();
  }, [audioRef, patientAlreadyJoined, patientJoined]);

  useEffect(() => {
    const interval = setInterval(() => {
      const duration = moment.duration(Date.now() - startTimeExpireCall);
      const second = duration.asSeconds();
      if (second > roomInfo.videoCallRingingDuration && !patientJoined && !patientAlreadyJoined) {
        if (roomInfo) handleEndCall(EndReason.Timeout);
        leaveRoom(onCloseVideo);
      }
    }, INTERVAL);
    return () => clearInterval(interval);
  }, [
    handleEndCall,
    startTimeExpireCall,
    patientJoined,
    roomInfo,
    patientAlreadyJoined,
    onCloseVideo,
    leaveRoom,
  ]);

  return (
    <div
      id="video-call-wrapper"
      className={classNames({
        [css.container]: !isMinimized,
        'container-minimized': isMinimized,
      })}
      ref={refContainer}
      role="button" // or another appropriate role
      tabIndex={0}
      onMouseDown={onMouseDown}
      onMouseMove={onMouseMove}
      onMouseUp={onMouseUp}
      onMouseLeave={onMouseLeave}>
      {status !== null && <StatusJoinCall status={status} />}
      <div className={css.videoCallBody}>
        {yourNetworkQuality < 2 && <NetworkQualityWarning name="Your" />}
        <img
          className={css.MinimizeCancel}
          src={MinimizeCancelImg}
          alt="button"
          onClick={() => {
            resetPositionMinimized(true);
            setIsMinimized(false);
          }}
        />
        <div className={css.videoHeader}>
          <VideoHeader
            avatar={<img src={avatar ?? ImgAvatarDefault} alt="ava" />}
            name={otherUserParticipant?.displayName ?? participantName}
            isMuted={otherUserParticipant?.muted}
            isMinimized={isMinimized}
            setIsMinimized={state => {
              resetPositionMinimized(false);
              setIsMinimized(state);
            }}
            flip={flip}
            setFlip={setFlip}
            speaking={speakingUsers.map(e => e.userId).includes(otherUserParticipant?.userId)}
          />
        </div>
        <div className={css.videoContainerParticipant}>
          <video-player-container
            id="video-call-participant"
            class={classNames({ hidden: !patientJoined, [css.flipped]: flip })}
          />
          {(!patientJoined || !participantCameraOn) && (
            <Waiting
              name={otherUserParticipant?.displayName ?? participantName}
              avatar={<img src={avatar ?? ImgAvatarDefault} alt="ava" />}
              text={
                !patientJoined || !participantInit
                  ? 'Video Calling...'
                  : `${otherUserParticipant?.displayName}'s camera is off`
              }
            />
          )}
          {speakingUsers.map(e => e.userId)?.includes(otherUserParticipant?.userId) &&
            isMinimized && <Image src={ImgSound} className={css.soundWave} />}
          <div className={css.memberName}>
            <MemberNameMinimized
              name={otherUserParticipant?.displayName ?? participantName}
              isMuted={otherUserParticipant?.muted}
            />
          </div>
        </div>
        <Recording patientAlreadyJoined={patientAlreadyJoined} recording={recording} />
        <div className={css.control}>
          <VideoControls
            onClose={() => {
              handleCloseVideo(patientAlreadyJoined ? EndReason.DoctorEnd : EndReason.DoctorReject);
            }}
            toggleMic={() => {
              if (isAttentionAudio) setIsAttentionAudio(false);
              toggleMic(currentUserParticipant?.muted);
            }}
            isAudioMuted={currentUserParticipant?.muted}
            isCameraOff={isCameraOff}
            isMinimized={isMinimized}
            deviceNotFound={userDevices.length === 0}
            toggleBlur={() => toggleBlur(!isBlur)}
            isBlur={isBlur}
            showBackgroundModal={() => setOpenVirtualBackground(true)}
            supportVirtualBackground={supportVirtualBackground}
            userDevices={userDevices}
            isAttentionAudio={isAttentionAudio}
          />
        </div>
        <div className={css.videoContainerSelf}>
          {speakingUsers.map(e => e.userId)?.includes(currentUserParticipant?.userId) && (
            <Image src={ImgSound} className={css.soundWave} />
          )}
          {isCameraOff && <CameraOff />}
          <video-player-container id="video-call-self" />
          <div className={css.memberName}>
            <MemberNameMinimized
              name={currentUserName}
              isMuted={currentUserParticipant?.muted}
              isLowNetwork={yourNetworkQuality < 2}
            />
          </div>
        </div>
        {patientJoined && (
          <div className={css.timeCounter}>
            <Counter room={roomInfo} />
          </div>
        )}

        {openVirtualBackground && (
          <VirtualBackgroundModal
            setOpen={setOpenVirtualBackground}
            mediaStream={mediaStream}
            userId={currentUserParticipant?.userId}
            selectedBg={selectedBg}
            setSelectedBg={setSelectedBg}
          />
        )}
      </div>
    </div>
  );
}

export default ZoomView;
